import { PropsWithChildren, useContext, useEffect, useState } from "react";
import { Spinner, ToolbarButton, ToolbarDivider, ToolbarGroup, makeStyles, shorthands, tokens } from "@fluentui/react-components";
import {
    InfoRegular
} from "@fluentui/react-icons";
import { useL10n } from "context/l10n-context";
import { ILocaleStrings } from "loc/locale.interfaces";
import { useNavigate, useParams } from "react-router-dom";
import { usePPA } from "context/ppa-context";
import { IApplicationRole } from "@interfaces/settings/permissions.interfaces";
import { IApiError } from "@interfaces/common.interfaces";
import LoadingErrorBar from "components/common/loading-error-bar/loading-error-bar";
import { getError } from "lib/utils";
import LoadingSpinner from "components/common/loading-spinner/loading-spinner";
import DeleteDialog from "components/common/delete-dialog/delete-dialog";
import { IApplicationRolesSectionProps } from "./application-roles-section.props";
import ApplicationRolesTiles from "../application-roles-tiles/application-roles-tiles";

const layoutStyles = makeStyles({
    mainWrapper: {
        //...shorthands.borderWidth('1px', '0'),
    }
});

export default function ApplicationRolesSection(props: IApplicationRolesSectionProps) {
    const styles = layoutStyles();
    const { t } = useL10n<ILocaleStrings>();
    const { getApiClient } = usePPA();
    const navigate = useNavigate();
    const [loading, setLoading] = useState<boolean>(false);
    const [apiError, setApiError] = useState<IApiError | null>(null);
    const [applicationRoles, setApplicationRoles] = useState<Array<IApplicationRole>>([]);
    const [deleteDialogProps, setDeleteDialogProps] = useState<{ apiUrl: string, message: string }>({ apiUrl: '', message: '' });
    const [deleteDialogIsOpen, setDeleteDialogIsOpen] = useState<boolean>(false);

    const getApplicationRoles = async (): Promise<Array<IApplicationRole>> => {
        try {
            const response = await getApiClient().get<Array<IApplicationRole>>(`/api/settings/permissions/application-roles`);
            return response.data;
        }
        catch (ex: unknown) {
            throw ex;
        }
    }

    const loadApplicationRoles = (showLoading: boolean) => {
        setLoading(showLoading);
        getApplicationRoles().then(
            (roles: Array<IApplicationRole>) => {
                setApplicationRoles(roles);
                setLoading(false);
            },
            (error: any) => {
                setApiError(getError(error));
                setLoading(false);
            });
    }

    useEffect(() => {
        loadApplicationRoles(true);
    }, []);

    const onAddApplicationRole = () => {
        //console.log(`onAddApplicationRole ->`);
        navigate(`new`, { relative: "route" });
    }
    const onEditApplicationRole = (id: number) => {
        //console.log(`onEditApplicationRole ->`, id);
        navigate(id.toString(), { relative: "route" });
    }
    const onEditApplicationRolePermissionLevel = (id: number) => {
        //console.log(`onEditApplicationRolePermissionLevel ->`, id);
        navigate(`${id.toString()}/permission-level`, { relative: "route" });
    }
    const onEditApplicationRoleMembership = (id: number) => {
        //console.log(`onEditApplicationRoleMembership ->`, id);
        navigate(`${id.toString()}/membership`, { relative: "route" });
    }
    const onDeleteApplicationRole = (id: number) => {
        //console.log(`onDeleteApplicationRole ->`, id);

        const applicationRole = applicationRoles.filter(r => r.id === id)[0];

        if (!applicationRole)
            return;

        setDeleteDialogProps({
            apiUrl: `/api/settings/permissions/application-roles/${id}`,
            message: t('ppaApplicationRoleDeleteMessage', { name: applicationRole.name })
        });
        setDeleteDialogIsOpen(true);
    }
    const onDeleteApplicationRoleSuccess = () => {
        setDeleteDialogIsOpen(false);
        loadApplicationRoles(false);
    }

    return (
        <>
            <ApplicationRolesTiles
                loading={loading}
                apiError={apiError}
                applicationRoles={applicationRoles}
                onAddApplicationRoleClick={onAddApplicationRole}
                onEditApplicationRoleClick={onEditApplicationRole}
                onEditApplicationRolePermissionLevelClick={onEditApplicationRolePermissionLevel}
                onDeleteApplicationRoleClick={onDeleteApplicationRole}
                onEditApplicationRoleMembershipClick={onEditApplicationRoleMembership} />
            <DeleteDialog
                isOpen={deleteDialogIsOpen}
                isOpenCallback={setDeleteDialogIsOpen}
                onSuccess={onDeleteApplicationRoleSuccess}
                title={t('ppaApplicationRoleDeleteTitle')}
                {...deleteDialogProps}
            />
        </>
    );
}